import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Mail, Send, Trash2, Loader2, Paperclip, Link2, AlertTriangle, Reply, CheckCircle2 } from "lucide-react";
import { useSignature, previewHtml } from "@/lib/emailCompose";
import { ClaudeBadge, ago, when } from "@/components/claude/ClaudeBadge";
import { NdaOverrideDialog, ndaGateOf } from "@/components/nda/NdaBits";

export const KIND_LABEL = { follow_up: "Follow-up", proposal: "Proposal", reply: "Reply", general: "Email" };

function Field({ label, children }) {
  return (
    <label className="grid grid-cols-[56px_1fr] items-center gap-2">
      <span className="text-xs text-slate-500">{label}</span>{children}
    </label>
  );
}

function ProposalNote({ p, confirm, setConfirm }) {
  if (p.block) {
    return <div data-testid="draft-proposal-block" className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"><AlertTriangle className="w-4 h-4 shrink-0" />Can't send yet: {p.block}</div>;
  }
  const mc = p.money_check;
  return (
    <div data-testid="draft-proposal-note" className="text-xs text-[#003E87] bg-[#003E87]/5 border border-[#003E87]/15 rounded-md px-3 py-2 space-y-0.5">
      <div className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> The {p.source === "provided" ? "uploaded " : ""}proposal PDF is attached when you send.</div>
      <div className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> The tracked view link replaces [proposal link], or is added at the end.</div>
      <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> The bid is marked sent (round {p.next_round}).</div>
      {mc?.mismatch && (
        <label data-testid="draft-money-mismatch" className="flex items-start gap-1.5 text-amber-800 pt-1">
          <input type="checkbox" data-testid="draft-confirm-mismatch" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" />
          {mc.explain || `The uploaded proposal says $${Number(mc.provided_total).toLocaleString()} and the bid total is $${Number(mc.live_total).toLocaleString()}.`} I checked this; send anyway.
        </label>
      )}
    </div>
  );
}

function SendBlockers({ d }) {
  if (!d.gmail.can_send) {
    return <div data-testid="draft-gmail-needed" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Connect Gmail with send access on the <Link to="/inbox" className="underline font-medium">Email Agent</Link> page to send this.</div>;
  }
  if (d.proposal && !d.proposal.can_send_bid) {
    return <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Your role can't send bids, so this proposal email can't go out from your account.</div>;
  }
  return null;
}

function DraftForm({ d, form, setForm, preview, sig }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-2">
      {d.reply_to && <div data-testid="draft-reply-to" className="flex items-center gap-1.5 text-xs text-slate-500"><Reply className="w-3.5 h-3.5" /> Reply in the thread with {d.reply_to.counterparty}: "{d.reply_to.subject}"</div>}
      <Field label="To"><Input data-testid="draft-to" value={form.to} onChange={set("to")} className="h-8 text-sm" /></Field>
      <Field label="Cc"><Input data-testid="draft-cc" value={form.cc} onChange={set("cc")} className="h-8 text-sm" placeholder="Optional" /></Field>
      <Field label="Subject"><Input data-testid="draft-subject" value={form.subject} onChange={set("subject")} className="h-8 text-sm" /></Field>
      {preview ? (
        <div data-testid="draft-preview" className="border border-slate-200 rounded-md p-3 min-h-[220px] max-h-[45vh] overflow-auto text-sm bg-white"
          dangerouslySetInnerHTML={{ __html: previewHtml(form.body, sig) }} />
      ) : (
        <Textarea data-testid="draft-body" rows={11} value={form.body} onChange={set("body")} className="text-sm" />
      )}
    </div>
  );
}

function useDraft(draftId, onClose) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  useEffect(() => {
    setD(null);
    if (!draftId) return;
    api.get(`/email/drafts/${draftId}`).then((r) => {
      setD(r.data);
      setForm({ to: r.data.to.join(", "), cc: (r.data.cc || []).join(", "), subject: r.data.subject, body: r.data.body });
    }).catch((e) => { toast.error(e.response?.data?.detail || "Could not open the draft"); onClose(); });
    // eslint-disable-next-line
  }, [draftId]);
  return { d, form, setForm };
}

export function DraftReviewDialog({ draftId, onOpenChange, onDone }) {
  const close = () => onOpenChange(false);
  const { d, form, setForm } = useDraft(draftId, close);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [ndaGate, setNdaGate] = useState(null);
  const sig = useSignature();
  const act = async (kind, ndaReason = null) => {
    if (kind === "discard" && !window.confirm("Discard this draft? It won't be sent.")) return;
    setBusy(kind);
    try {
      if (kind === "discard") {
        await api.post(`/email/drafts/${draftId}/discard`);
        toast.success("Draft discarded");
      } else {
        await api.put(`/email/drafts/${draftId}`, form);
        if (kind === "send") {
          const r = await api.post(`/email/drafts/${draftId}/send`, { confirm_mismatch: confirmMismatch, ...(ndaReason ? { nda_override_reason: ndaReason } : {}) }, { timeout: 180000 });
          toast.success(r.data.note ? `Email sent. ${r.data.note}` : "Email sent");
        } else toast.success("Draft saved");
      }
      onDone?.();
      if (kind !== "save") close();
    } catch (e) {
      const gate = ndaGateOf(e);
      if (gate) setNdaGate(gate);
      else toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Something went wrong");
    }
    finally { setBusy(""); }
  };
  const open = !!draftId;
  const isDraft = d?.status === "draft";
  const blocked = !d || !d.gmail.can_send || (d.proposal && (d.proposal.block || !d.proposal.can_send_bid || (d.proposal.money_check?.mismatch && !confirmMismatch)));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <NdaOverrideDialog gate={ndaGate} onClose={() => setNdaGate(null)} onRetry={(reason) => act("send", reason)} />
      <DialogContent data-testid="draft-review-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Mail className="w-4 h-4 text-[#003E87]" /> Review {d ? KIND_LABEL[d.kind]?.toLowerCase() : "email"}
            {d?.via === "claude" && <ClaudeBadge testid="draft-claude-badge" label="Prepared by Claude" />}
          </DialogTitle>
          <DialogDescription>
            {d ? <>{d.bid_name || d.lead_name ? <>For <b>{d.bid_name || d.lead_name}</b> · </> : null}prepared {ago(d.created_at)}. Nothing is sent until you click Send.</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {!d || !form ? <Loader2 className="w-5 h-5 animate-spin text-[#003E87] mx-auto my-10" /> : !isDraft ? (
          <div data-testid="draft-closed" className="text-sm text-slate-600">This email was {d.status} {when(d.sent_at || d.discarded_at)}.{d.sent_note ? ` ${d.sent_note}` : ""}</div>
        ) : (
          <div className="space-y-3">
            {d.proposal && <ProposalNote p={d.proposal} confirm={confirmMismatch} setConfirm={setConfirmMismatch} />}
            <DraftForm d={d} form={form} setForm={setForm} preview={preview} sig={sig} />
            <SendBlockers d={d} />
          </div>
        )}
        {isDraft && form && (
          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button variant="ghost" data-testid="draft-discard" disabled={!!busy} onClick={() => act("discard")} className="text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4 mr-1" /> Discard</Button>
              <Button variant="ghost" data-testid="draft-toggle-preview" onClick={() => setPreview((v) => !v)}>{preview ? "Edit" : "Preview"}</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="draft-save" disabled={!!busy} onClick={() => act("save")}>{busy === "save" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save</Button>
              <Button data-testid="draft-send" disabled={!!busy || blocked} onClick={() => act("send")} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
                {busy === "send" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}{busy === "send" ? "Sending…" : "Send"}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DraftRow({ d, onOpen, showContext = true }) {
  return (
    <div data-testid={`email-draft-row-${d.draft_id}`} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5 shrink-0">{KIND_LABEL[d.kind]}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-800 truncate">{d.subject}</div>
        <div className="text-[11px] text-slate-400 truncate">
          To {d.to.join(", ")}{showContext && (d.bid_name || d.lead_name) ? ` · ${d.bid_name || d.lead_name}` : ""} · {ago(d.created_at)}
          {d.via === "claude" && <> · <ClaudeBadge /></>}
        </div>
      </div>
      <Button size="sm" variant="outline" data-testid={`email-draft-review-${d.draft_id}`} onClick={() => onOpen(d.draft_id)} className="h-8 shrink-0">Review</Button>
    </div>
  );
}

function useDrafts(params) {
  const [rows, setRows] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => api.get("/email/drafts", { params }).then((r) => setRows(r.data)).catch(() => setRows([])),
    // eslint-disable-next-line
    [key]);
  useEffect(() => { load(); }, [load]);
  return { rows, load };
}

export function DraftsPanel({ initialDraft, hideEmpty = false, onChange }) {
  const { rows, load } = useDrafts({});
  const [open, setOpen] = useState(initialDraft || null);
  const reload = () => { load(); onChange?.(); };
  if (!rows) return null;
  if (hideEmpty && !rows.length && !open) return null;
  return (
    <div data-testid="inbox-drafts-panel" className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
      <div className="text-sm font-semibold text-black mb-1">Email drafts waiting for you</div>
      <div className="text-xs text-slate-500 mb-2">Prepared for you, mostly by Claude. Nothing goes out until you review it and click Send.</div>
      {rows.length ? rows.map((d) => <DraftRow key={d.draft_id} d={d} onOpen={setOpen} />)
        : <div data-testid="inbox-drafts-empty" className="text-center py-8 text-sm text-slate-400">No drafts waiting. When Claude prepares an email for you, it shows up here.</div>}
      <DraftReviewDialog draftId={open} onOpenChange={(o) => { if (!o) setOpen(null); }} onDone={reload} />
    </div>
  );
}

function DraftChips({ rows, onOpen }) {
  return rows.slice(0, 3).map((d) => (
    <button key={d.draft_id} data-testid={`bid-draft-open-${d.draft_id}`} onClick={() => onOpen(d.draft_id)}
      className="rounded-full bg-white border border-[#003E87]/20 px-2.5 py-0.5 truncate max-w-[240px] hover:border-[#003E87]/50 transition-colors">
      {KIND_LABEL[d.kind]}: {d.subject}
    </button>
  ));
}

export function DraftsCard({ bidId, leadId, variant = "card" }) {
  const { rows, load } = useDrafts(bidId ? { bid_id: bidId } : { lead_id: leadId });
  const [open, setOpen] = useState(null);
  if (!rows || !rows.length) return <DraftReviewDialog draftId={open} onOpenChange={(o) => { if (!o) setOpen(null); }} onDone={load} />;
  const title = `${rows.length} email${rows.length === 1 ? "" : "s"} waiting for your review`;
  return (
    <>
      {variant === "strip" ? (
        <div data-testid="bid-email-drafts" className="shrink-0 bg-[#003E87]/5 border-b border-[#003E87]/15 px-5 py-1.5 flex flex-wrap items-center gap-2 text-xs text-[#003E87]">
          <Mail className="w-4 h-4 shrink-0" /><b>{title}</b>
          <DraftChips rows={rows} onOpen={setOpen} />
          {rows.length > 3 && <Link to="/inbox?tab=drafts" className="underline">+{rows.length - 3} more</Link>}
        </div>
      ) : (
        <div data-testid="lead-email-drafts" className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700"><Mail className="w-3.5 h-3.5 text-[#003E87]" /> {title}</div>
          {rows.map((d) => <DraftRow key={d.draft_id} d={d} onOpen={setOpen} showContext={false} />)}
        </div>
      )}
      <DraftReviewDialog draftId={open} onOpenChange={(o) => { if (!o) setOpen(null); }} onDone={load} />
    </>
  );
}
